import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import useChatHistory from '@/hooks/useChatHistory';
import { IChatMessage, IPrompt } from '@/types';
import axiosClient from '@/utils/axiosClient';

interface PromptChatProps {
  prompt: IPrompt;
}

const PromptChat = ({ prompt }: PromptChatProps) => {
  const queryClient = useQueryClient();
  const chatHistoryQuery = useChatHistory(prompt.id);

  const [message, setMessage] = useState('');

  const key = `v1/prompts/${prompt.id}/history`;

  // Reference to the dummy div for autoscroll
  const dummyDivRef = useRef<HTMLDivElement>(null);

  // Define the mutation using useMutation
  const mutation = useMutation(
    (newMessage: string) => axiosClient.post(`v1/prompts/${prompt.id}/chat`, { message: newMessage }),
    {
      // Optimistically update the chat history before the mutation is completed
      onMutate: async (newMessage: string) => {
        // Cancel any outgoing refetches to prevent them from overwriting optimistic updates
        await queryClient.cancelQueries(key);

        // Snapshot the previous chat history
        const previousChatHistory = queryClient.getQueryData<IChatMessage[]>([key]);

        // Optimistically update the cache with the user's new message
        if (previousChatHistory) {
          queryClient.setQueryData<IChatMessage[]>(
            [key],
            [...previousChatHistory, { role: 'user', content: newMessage, created_at: new Date().toISOString() }],
          );
        }

        // Return a context object with the snapshotted previous chat history
        return { previousChatHistory };
      },
      // On success, append the assistant's response
      onSuccess: (response) => {
        queryClient.setQueryData<IChatMessage[]>([key], (old) =>
          old
            ? [...old, { role: 'assistant', content: response.data.message, created_at: new Date().toISOString() }]
            : [{ role: 'assistant', content: response.data.message, created_at: new Date().toISOString() }],
        );
      },
      // If the mutation fails, rollback to the previous chat history
      onError: (err, _, context) => {
        if (context?.previousChatHistory) {
          queryClient.setQueryData<IChatMessage[]>([key], context.previousChatHistory);
        }
        console.error('Error sending message:', err);
        // Optionally, you can show a toast or notification here to inform the user
      },
      // After mutation either success or error, refetch the chat history to ensure data consistency
      onSettled: () => {
        queryClient.invalidateQueries([key]);
      },
    },
  );

  const sendChat = () => {
    if (!message.trim()) return;

    // Trigger the mutation
    mutation.mutate(message);

    // Clear the input field
    setMessage('');
  };

  // Autoscroll effect using dummy div
  useEffect(() => {
    if (dummyDivRef.current) {
      dummyDivRef.current.scrollIntoView({ behavior: 'instant' });
    }
  }, [chatHistoryQuery.data, mutation.isLoading]);

  // Handle loading and error states from fetching chat history
  if (chatHistoryQuery.isLoading) {
    return <div>Loading chat history...</div>;
  }

  if (chatHistoryQuery.isError) {
    return <div>Error loading chat history.</div>;
  }

  const messages = chatHistoryQuery.data || [];

  return (
    <Card className="flex flex-col h-[500px]">
      <CardHeader>
        <CardTitle>AI Assistant</CardTitle>
        <CardDescription>Chat with the AI assistant to get suggestions for your prompt.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col flex-1">
        <div className="flex-1">
          <ScrollArea className="h-72">
            {messages.map((msg, index) => (
              <div key={index} className={`mb-4 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                <span
                  className={`inline-block p-2 rounded-lg ${
                    msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  }`}
                >
                  {msg.content}
                </span>

                {msg.role === 'user' && <div className="text-sm text-gray-600">{msg.user?.name}</div>}
              </div>
            ))}
            {mutation.isLoading && (
              <div className="mb-4 text-left">
                <span className="inline-block p-2 italic rounded-lg bg-muted">AI Assistant is typing...</span>
              </div>
            )}
            <div ref={dummyDivRef} />
          </ScrollArea>
        </div>
        <div className="flex gap-2 mt-4">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1"
            disabled={mutation.isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendChat();
              }
            }}
          />
          <Button onClick={sendChat} disabled={mutation.isLoading || !message.trim()}>
            {mutation.isLoading ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PromptChat;
